// apps/web/src/routes/__root.tsx
import { createRootRoute, Outlet, Link, useNavigate, useLocation } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@forgetrack/auth';
import { LanguageSwitcher } from '@forgetrack/i18n';
import { LoadingSpinner } from '@forgetrack/ui';
import { ENGINE_COLORS as C, ENGINE_FONTS as F } from '@forgetrack/theme';
import { ContextHelpProvider } from '../components/engine/ContextHelpProvider';
import { useCurrentUser } from '../lib/api/useCurrentUser';
import { ContextHelpToggle } from '../components/engine/HelpBubble';
import { SubscriptionBadge } from '../components/SubscriptionBadge';
import { useProjects } from '../lib/api/projects';

function RootLayout() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const checkedRef = useRef(false);

  // Public routes — auth guard must NOT fire on these
  const PUBLIC_PATHS = ['/signup', '/welcome', '/share'];
  const isPublicRoute = PUBLIC_PATHS.some(p => location.pathname.startsWith(p));

  // ── MSAL popup detection ───────────────────────────────────────────────────
  // Step-up auth (acquireTokenPopup, see @forgetrack/api-client) opens a popup
  // whose redirectUri is window.location.origin — i.e. "/", a PROTECTED route.
  // Entra returns the auth response there, so the popup boots this whole SPA.
  // Without this guard the effect below sees "not authenticated" (MSAL has not
  // finished reading the response yet) and calls login(), which navigates the
  // popup AWAY from the redirect URI to a fresh sign-in page. MSAL's monitor
  // then never observes the response and gives up after its 60s
  // monitorWindowTimeout, so every step-up failed silently with the popup left
  // sitting on a Microsoft "Sign in" screen.
  //
  // A popup opened by window.open has a non-self opener; a normal tab does not.
  // Inside one, this component must do nothing and let MSAL finish.
  const isAuthPopup =
    typeof window !== 'undefined' && window.opener != null && window.opener !== window;

  // useAuth (not useRequireAuth) — we handle the redirect ourselves so we can
  // skip it on public routes like /signup.
  const { isInitialized, isLoading, isAuthenticated, login } = useAuth();

  // Redirect to login only for protected routes
  useEffect(() => {
    if (isAuthPopup) return;
    if (isPublicRoute) return;
    if (isInitialized && !isAuthenticated && !isLoading) {
      login();
    }
  }, [isAuthPopup, isPublicRoute, isInitialized, isAuthenticated, isLoading, login]);

  // ── First-run detection ────────────────────────────────────────────────────
  const { data: projects, loading: projectsLoading } = useProjects(
    isAuthenticated && !isPublicRoute && !isAuthPopup
  );

  useEffect(() => {
    if (isAuthPopup) return;
    if (!isAuthenticated || isPublicRoute || projectsLoading) return;
    if (projects === null) return;
    if (checkedRef.current) return;
    if (localStorage.getItem('ft_onboarded')) return;

    checkedRef.current = true;

    if (projects.length === 0) {
      navigate({ to: '/welcome', replace: true });
    }
  }, [isAuthPopup, isAuthenticated, isPublicRoute, projectsLoading, projects, navigate]);
  // ── End first-run detection ────────────────────────────────────────────────

  // Render nothing inside the MSAL popup — no nav, no data fetches, no router
  // work. MSAL reads the auth response straight off the popup's URL and closes
  // the window; anything rendered there is wasted at best and harmful at worst.
  if (isAuthPopup) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LoadingSpinner size={32} label="Completing sign-in..." />
      </div>
    );
  }

  if (!isPublicRoute && (!isInitialized || isLoading)) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LoadingSpinner size={48} label="Initializing ForgeTrack..." />
      </div>
    );
  }

  if (!isPublicRoute && !isAuthenticated) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <LoadingSpinner size={48} label="Redirecting to login..." />
      </div>
    );
  }

  // Public routes (signup, welcome, share) render directly — no sidebar/nav
  if (isPublicRoute) {
    return <Outlet />;
  }

  return (
    <ContextHelpProvider>
      <div style={{ display: 'flex', minHeight: '100vh' }}>
        {/* Sidebar */}
        <nav style={{
          width: 240,
          background: C.surface,
          borderRight: `1px solid ${C.faint}`,
          display: 'flex',
          flexDirection: 'column',
          padding: '20px 0',
          flexShrink: 0,
        }}>
          <div style={{
            padding: '8px 20px 24px',
            fontFamily: F.display,
            fontWeight: 700,
            fontSize: 20,
            color: C.neon,
            letterSpacing: '-0.02em',
          }}>
            ⚡ ForgeTrack
          </div>

          <NavLinks />
          <div style={{ flex: 1 }} />

          {/* Subscription tier badge — owner/admin only */}
          <div style={{ padding: '8px 16px 4px' }}>
            <SubscriptionBadge />
          </div>

          <div style={{ padding: '4px 16px' }}>
            <ContextHelpToggle />
          </div>
          <div style={{ padding: '4px 16px 8px' }}>
            <LanguageSwitcher openRight />
          </div>

          <SignOutPanel />
        </nav>

        <main style={{ flex: 1, overflow: 'auto' }}>
          <Outlet />
        </main>
      </div>
    </ContextHelpProvider>
  );
}

function NavLinks() {
  const location = useLocation();
  const { isAdmin } = useCurrentUser();

  const NAV_ITEMS = [
    { id: 'dashboard',    label: 'Dashboard',     icon: '⚡', path: '/dashboard' },
    { id: 'projects',     label: 'Projects',      icon: '📐', path: '/projects' },
    { id: 'sessions',     label: 'Sessions',      icon: '⏱',  path: '/sessions' },
    { id: 'work-items',   label: 'Work Items',    icon: '📋', path: '/work-items' },
    { id: 'contributors', label: 'Contributors',  icon: '👥', path: '/contributors' },
    { id: 'decisions',    label: 'Decisions',     icon: '🧭', path: '/decisions' },
    { id: 'import',       label: 'Import',        icon: '📥', path: '/import' },
    { id: 'effort',       label: 'Effort Engine', icon: '🔮', path: '/effort' },
    { id: 'uncertainty',  label: 'Uncertainty',   icon: '🔬', path: '/technical-uncertainty' },
    { id: 'reports',      label: 'Reports',       icon: '📊', path: '/reports' },
    { id: 'ai-reports',   label: 'AI Reports',    icon: '🤖', path: '/ai-reports' },
    { id: 'git',          label: 'Git',           icon: '🔗', path: '/git' },
    { id: 'investor',     label: 'Investor',      icon: '📈', path: '/investor' },
    { id: 'compliance',   label: 'Compliance',    icon: '🛡', path: '/compliance' },
    { id: 'audit',         label: 'Audit Log',     icon: '📋', path: '/audit' },
    { id: 'billing',      label: 'Billing',       icon: '💳', path: '/billing', adminOnly: true },
    { id: 'ai-models',          label: 'AI Models',         icon: '🧠', path: '/ai-models', adminOnly: true },
    { id: 'classifier-tuning',  label: 'Classifier Tuning', icon: '🎯', path: '/classifier-tuning', adminOnly: true },
    { id: 'prompts',            label: 'Prompts',           icon: '📝', path: '/prompts', adminOnly: true },
    { id: 'integrations', label: 'Integrations',  icon: '🔑', path: '/integrations', adminOnly: true },
    { id: 'settings',     label: 'Settings',      icon: '⚙️', path: '/settings', adminOnly: true },
  ] as const;

  const visibleItems = NAV_ITEMS.filter(item => !('adminOnly' in item && item.adminOnly) || isAdmin);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '0 10px' }}>
      {visibleItems.map((item) => {
        const active = location.pathname.startsWith(item.path);
        return (
          <Link
            key={item.id}
            to={item.path}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '11px 12px',
              borderRadius: 8,
              fontSize: 15,
              fontWeight: active ? 600 : 500,
              color: active ? C.neon : C.textSoft,
              background: active ? `${C.neon}14` : 'transparent',
              textDecoration: 'none',
              transition: 'all 0.15s ease',
              border: active ? `1px solid ${C.neon}28` : '1px solid transparent',
              fontFamily: F.display,
            }}
          >
            <span style={{ fontSize: 17 }}>{item.icon}</span>
            {item.label}
            {item.id === 'billing' && <BillingWarningDot />}
          </Link>
        );
      })}
    </div>
  );
}

function SignOutPanel() {
  const { account, logout } = useAuth();

  return (
    <div style={{
      padding: '8px 16px 4px',
      marginTop: 4,
      borderTop: `1px solid ${C.faint}`,
      fontFamily: F.display,
    }}>
      <div style={{ fontSize: 11, color: C.dim, marginBottom: 4 }}>SIGNED IN AS</div>
      <div style={{
        fontSize: 12,
        color: C.textSoft,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        marginBottom: 8,
      }}>
        {account?.username ?? account?.name ?? '—'}
      </div>
      <button
        onClick={() => logout()}
        style={{
          width: '100%',
          padding: '6px 8px',
          fontSize: 11,
          fontFamily: F.mono,
          letterSpacing: '0.08em',
          color: C.muted,
          background: 'transparent',
          border: `1px solid ${C.faint}`,
          borderRadius: 6,
          cursor: 'pointer',
        }}
      >
        SIGN OUT
      </button>
    </div>
  );
}

function BillingWarningDot() {
  // Silent — never crashes the sidebar. Shows a dot only when subscription
  // status is unhealthy and the API is reachable with a valid session.
  const [warn, setWarn] = useState<'coral' | 'amber' | null>(null);
  useEffect(() => {
    // Use the same API client pattern as the rest of the app
    const controller = new AbortController();
    (async () => {
      try {
        const res = await fetch('/api/v1/billing/subscription', {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!res.ok) return;
        const d = await res.json();
        const s = d?.subscriptionStatus ?? d?.status ?? '';
        if (s === 'PastDue' || s === 'Unpaid') setWarn('coral');
        else if (s === 'Trialing' || s === 'Canceled') setWarn('amber');
      } catch { /* silent */ }
    })();
    return () => controller.abort();
  }, []);
  if (!warn) return null;
  const color = warn === 'coral' ? C.coral : C.amber;
  return <span style={{ width: 7, height: 7, borderRadius: '50%', marginLeft: 'auto', background: color, boxShadow: `0 0 6px ${color}` }} />;
}

export const Route = createRootRoute({ component: RootLayout });
